import { Action, State, StateContext, StateToken } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { AdministrationStateModel } from './administration-state.model';
import {
  ChangeUserState,
  CleanExportData,
  ExportUserData,
  GetEMail,
  GetUsers,
  SetEMailSettings,
  SetUserToAdmin,
} from './administration.actions';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  EExportBrille,
  EMailService,
  TGetMailSwitchesDTO,
  TUserAdminplusCompanyDTO,
  TUserStateDTO,
  UserDomainService,
  UserExportService,
  UserStatusService,
} from '@api';
import { ActionState } from '@interfaces';

const ADMINISTRATION_STATE_TOKEN: StateToken<AdministrationStateModel> =
  new StateToken('administrationState');

@State<AdministrationStateModel>({
  name: ADMINISTRATION_STATE_TOKEN,
  defaults: {
    actionState: ActionState.create(),
    actionStateUserCSV: ActionState.create(),
    userList: [],
    userDataCSV: null,
    eMailSettings: null,
  },
})
@Injectable()
export class AdministrationState {
  constructor(
    private readonly userDomainService: UserDomainService,
    private readonly userExportService: UserExportService,
    private readonly userStatusService: UserStatusService,
    private readonly emailService: EMailService
  ) {}

  @Action(GetUsers)
  public getUsers(
    context: StateContext<AdministrationStateModel>
  ): Observable<TUserAdminplusCompanyDTO[]> {
    context.patchState({
      ...context,
      actionState: ActionState.onStart(),
    });
    return this.userDomainService.apiUserDomainGet().pipe(
      tap({
        next: (list: TUserAdminplusCompanyDTO[]) => {
          context.patchState({
            ...context,
            userList: list,
            actionState: ActionState.onDone(),
          });
        },
        error: () => {
          context.patchState({
            ...context,
            actionState: ActionState.onError(true),
          });
        },
      })
    );
  }

  @Action(CleanExportData)
  public cleanExportData(context: StateContext<AdministrationStateModel>) {
    context.patchState({
      ...context,
      userDataCSV: null,
      actionStateUserCSV: ActionState.onDone(),
    });
  }

  @Action(ExportUserData)
  public exportUserData(
    context: StateContext<AdministrationStateModel>,
    action: ExportUserData
  ): Observable<string> {
    context.patchState({
      ...context,
      actionStateUserCSV: ActionState.onStart(),
    });
    const exportGlasses: EExportBrille = action?.glasses
      ? EExportBrille[action.glasses]
      : EExportBrille.NUMBER_0;
    return this.userExportService.apiUserExportGet(exportGlasses).pipe(
      tap({
        next: (csvString: string) => {
          context.patchState({
            ...context,
            userDataCSV: {
              csv: csvString,
              fileName:
                action.filename === null
                  ? exportGlasses === EExportBrille.NUMBER_1562
                    ? 'co2-benutzer'
                    : 'h2-benutzer'
                  : action.filename,
            },
            actionStateUserCSV: ActionState.onDone(),
          });
        },
        error: () => {
          context.patchState({
            ...context,
            userDataCSV: null,
            actionStateUserCSV: ActionState.onError(true),
          });
        },
      })
    );
  }

  @Action(ChangeUserState)
  public changeUserState(
    context: StateContext<AdministrationStateModel>,
    action: ChangeUserState
  ) {
    context.patchState({
      ...context,
      actionState: ActionState.onStart(),
    });

    const userId = action.userId;
    const userStateDTO: TUserStateDTO = {
      set_user_state_id: action.setUserStateId,
    };
    return this.updateUserStateReloadUsers(context, userId, userStateDTO);
  }

  @Action(SetUserToAdmin)
  public setUserToAdmin(
    context: StateContext<AdministrationStateModel>,
    action: SetUserToAdmin
  ) {
    context.patchState({
      ...context,
      actionState: ActionState.onStart(),
    });

    const userId = action.userId;
    const userStateDTO: TUserStateDTO = {
      set_user_is_company_admin: action.setIsUserCompanyAdmin,
    };

    return this.updateUserStateReloadUsers(context, userId, userStateDTO);
  }

  @Action(GetEMail)
  public getEMail(context: StateContext<AdministrationStateModel>) {
    context.patchState({
      ...context,
      actionState: ActionState.onStart(),
    });
    return this.emailService.apiEMailGet().pipe(
      tap({
        next: (response: TGetMailSwitchesDTO) => {
          context.patchState({
            ...context,
            eMailSettings: response,
            actionState: ActionState.onDone(),
          });
        },
        error: () => {
          context.patchState({
            ...context,
            actionState: ActionState.onError(true),
          });
        },
      })
    );
  }

  @Action(SetEMailSettings)
  public setEMailSettings(
    context: StateContext<AdministrationStateModel>,
    action: SetEMailSettings
  ) {
    context.patchState({
      ...context,
      actionState: ActionState.onStart(),
    });
    return this.emailService.apiEMailPut(action.dto).pipe(
      tap({
        next: () => {
          context.patchState({
            ...context,
            actionState: ActionState.onDone(),
          });
        },
        error: () => {
          context.patchState({
            ...context,
            actionState: ActionState.onError(true),
          });
        },
      })
    );
  }

  private updateUserStateReloadUsers(
    context: StateContext<AdministrationStateModel>,
    userId: number,
    userStateDTO: TUserStateDTO
  ): Observable<any> {
    return this.userStatusService
      .apiUserStatusUserIdPut(userId, userStateDTO)
      .pipe(
        tap({
          next: () => {
            context.patchState({
              ...context,
              actionState: ActionState.onDone(),
            });
            context.dispatch(new GetUsers());
          },
          error: () => {
            context.patchState({
              ...context,
              actionState: ActionState.onError(true),
            });
          },
        })
      );
  }
}
